import * as React from 'react';

import { Button } from '@/components/ui/button';

// =============================================================================
// admin-landing-screen.tsx — LANDING admin publique (racine « / », §2.1).
// =============================================================================
// Remplace l'ancien PingScreen (S0) à la racine : page d'accueil publique de
// l'espace administrateur (identité produit + baseline + FOOTER + CTA de
// connexion). Structure sémantique (WCAG 2.1 AA) : <section> titrée, <footer>,
// CTA étiqueté, navigation clavier native.
//
// CTA de connexion — NAVIGATION PLEINE PAGE, PAS un fetch, PAS une route SPA :
//   `GET /admin/auth/login` est un REDIRECT navigateur (302 → fournisseur
//   d'identité). Le CTA est donc une ancre <a href> qui provoque un chargement
//   de page complet vers l'endpoint API, jamais un appel TanStack Query.
//   RÉSOLUTION DE L'URL : les clients HTTP de la SPA utilisent un `baseUrl` vide
//   (chemins relatifs — SPA et API partagent l'origine, ou un reverse proxy route
//   `/admin/*`). L'URL de login est donc le chemin relatif `/admin/auth/login`.
//
// DIFFÉRÉ-VISIBLE : le CTA est livré maintenant mais ne devient FONCTIONNEL
// qu'après le câblage OIDC/Hydra (Wave A). L'ancre pointe vers l'endpoint réel :
// aucun faux comportement n'est simulé (pas de handler factice, pas de fausse
// session) — tant que l'IdP n'est pas déployé, la navigation aboutit à la réponse
// réelle de l'API.

/** Endpoint de connexion admin (redirect 302 → IdP). Chemin relatif : baseUrl API = ''. */
const ADMIN_LOGIN_URL = '/api/admin/auth/login';

export function AdminLandingScreen(): React.JSX.Element {
  const year = new Date().getFullYear();

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-12">
      <section aria-labelledby="landing-title" className="space-y-6 text-center">
        <p className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          JEKO
        </p>
        <h1
          id="landing-title"
          className="text-4xl font-semibold tracking-tight text-foreground"
        >
          Portail de référencement des prestataires
        </h1>
        <p className="mx-auto max-w-prose text-lg text-muted-foreground">
          Plateforme d&apos;administration des campagnes de référencement
          fournisseurs : collecte des taux, analyse consolidée et publication des
          benchmarks anonymisés.
        </p>
        <div className="flex flex-col items-center gap-2 pt-2">
          <Button asChild size="lg">
            <a href={ADMIN_LOGIN_URL}>Accéder à mon espace</a>
          </Button>
          <p className="text-sm text-muted-foreground">
            Connexion sécurisée via le fournisseur d&apos;identité de
            l&apos;organisation.
          </p>
          {/* OPTION B (DEC-14, IdP-only) : aide générique, sans promesse de reset/déblocage
              côté application (gérés par le fournisseur d'identité). Lien de contact JeKo. */}
          <p className="text-sm text-muted-foreground">
            Un problème de connexion ?{' '}
            <a
              href="mailto:contact@jeko.biz"
              className="rounded-sm underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Contactez le support JeKo
            </a>
            .
          </p>
        </div>

        {/* Entrée secondaire FOURNISSEUR (bug prod onboarding) : un prestataire ne doit plus avoir
            à connaître l'URL « /onboarding ». Discrète (sous un séparateur, variant outline) pour ne
            pas concurrencer la CTA admin primaire. Ancre <a> vers la route SPA « /onboarding »,
            cohérente avec les autres liens de cette landing (mentions légales, CGU). Lien nommé +
            focus visible (buttonVariants) → WCAG 2.1 AA. */}
        <div className="flex flex-col items-center gap-2 border-t border-border pt-6">
          <p className="text-sm text-muted-foreground">
            Vous êtes un fournisseur référencé ?
          </p>
          <Button asChild variant="outline">
            <a href="/onboarding">Espace fournisseur</a>
          </Button>
        </div>
      </section>

      {/* Footer §2.1 l.562 : mentions légales + lien CGU (→ /cgu) + contact JeKo. */}
      <footer className="space-y-3 border-t border-border pt-6 text-center text-sm text-muted-foreground">
        <nav aria-label="Liens légaux et contact">
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <li>
              <a
                href="/mentions-legales"
                className="rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                Mentions légales
              </a>
            </li>
            <li>
              <a
                href="/cgu"
                className="rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                CGU
              </a>
            </li>
            <li>
              <a
                href="mailto:contact@jeko.biz"
                className="rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                Contact JeKo
              </a>
            </li>
          </ul>
        </nav>
        <p>© {year} JEKO Référencement. Tous droits réservés.</p>
      </footer>
    </div>
  );
}
